import { Select, SelectItem, Tab, Tabs } from 'carbon-components-react';
import './SBAForm.scss';
import useSWR from 'swr';
import PageLayout from '../../components/PageLayout';
import { useQueryParams } from '../../utils/useQueryParams';
import { PlusCircle } from 'react-feather';
import { useClassAccessList } from '../../api/class.service';
import { useUrlState } from '../../utils/useUrlState';
import { useState, useEffect } from 'react';
import useCurrentAcademicTerm from '../../utils/useCurrentAcademicTerm';
import CreateSBAModal from './CreateSBAModal';
import { ReactComponent as Subject } from '@/assets/images/card-icons/subject.svg';
import { ReactComponent as Certificate } from '@/assets/images/card-icons/certificate.svg';
import { Tag } from 'carbon-components-react';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { RenderIf } from '@/components/RenderIf';
import { getQueryString } from '@/utils/common.util';

export default function SBASubjectsView() {
  const pageTitle = `View SBA Subjects`;

  const {
    classId: urlClassId,
    termId: urlTermId,
    academicYearId: urlAcademicYearId,
  } = useQueryParams('classId', 'termId', 'academicYearId');
  const [query, setQuery] = useUrlState({
    classId: urlClassId ?? '',
    termId: urlTermId ?? '',
    academicYearId: urlAcademicYearId ?? '',
  });

  const { academicYears, terms } = useCurrentAcademicTerm({
    academicYearId: query.academicYearId,
    setQuery,
  });

  const { classAccessList: classes } = useClassAccessList();

  const { data: subjects, isSubjectsLoading } = useSWR(
    query.classId
      ? `academic/grading/subject-sba-status?${getQueryString(query)}`
      : null,
  );

  const [isOpen, setIsOpen] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const history = useHistory();
  useEffect(() => {
    if (query.classId) {
      setActiveTab(1);
    }
  }, [query.classId]);

  const handleClassClick = (classItem) => {
    setQuery({ ...query, classId: classItem.id.toString() });
    setActiveTab(1);
  };

  const handleBackToClasses = () => {
    setQuery({ ...query, classId: '' });
    setActiveTab(0);
  };

  return (
    <PageLayout
      breadcrumbs={[
        { label: 'Academics', to: '/academic' },
        { label: pageTitle, active: true },
      ]}
      title={
        <div className="d-flex align-items-center" style={{ gap: '30px' }}>
          <h1 className="h-20">{pageTitle}</h1>{' '}
          <button
            className="text-12 text-primary d-flex align-items-center font-bold pointer clean-btn "
            style={{ gap: '4px' }}
            onClick={() => setIsOpen('create-sba')}
          >
            <PlusCircle size="14" strokeWidth="2px" />
            <span>Create New SBA</span>
          </button>
        </div>
      }
      isLoading={isSubjectsLoading && activeTab === 1}
    >
      <p>Select options to view SBAs of a class </p>

      <div className="card-row-3 my-20">
        <Select
          labelText="Academic Year"
          onChange={(e) =>
            setQuery({
              academicYearId: e.target.value,
              termId: '',
              classId: '',
            })
          }
          value={query.academicYearId}
        >
          <SelectItem value="" text="" />
          {academicYears?.map((year) => (
            <SelectItem key={year.id} value={year.id} text={year.description} />
          ))}
        </Select>
        <Select
          labelText="Term"
          disabled={!query.academicYearId}
          onChange={(e) => setQuery({ termId: e.target.value, classId: '' })}
          value={query.termId}
        >
          <SelectItem value="" text="" />
          {terms?.map((term) => (
            <SelectItem key={term.id} value={term.id} text={term.description} />
          ))}
        </Select>
      </div>

      <Tabs selected={activeTab} className="mt-20 sba-tabs--readonly">
        <Tab id="tab-classes" label="Classes">
          <RenderIf condition={classes?.length}>
            <p className="text-14 mb-20 text-grey">
              Click on a class to view its subjects
            </p>

            <div className="card-row-4">
              {classes?.map((classItem) => (
                <SBAClassCard
                  key={classItem.id}
                  classItem={classItem}
                  onClick={() => handleClassClick(classItem)}
                />
              ))}
            </div>
          </RenderIf>

          <RenderIf condition={!classes?.length}>
            <p style={{ color: '#666' }}>
              No classes found for the selected filters.
            </p>
          </RenderIf>
        </Tab>

        <Tab id="tab-subjects" label="Subjects">
          <RenderIf condition={activeTab === 1}>
            <div className="d-flex gap-40 align-items-center">
              <div className="d-flex align-items-center mb-16">
                <button
                  className="text-12 text-primary d-flex align-items-center font-bold pointer clean-btn gap-4"
                  onClick={handleBackToClasses}
                >
                  <span>← Back to Classes</span>
                </button>
              </div>
              <RenderIf condition={query.classId}>
                <p className="text-14 mb-20 text-grey">
                  <strong>
                    {
                      classes?.find((c) => c.id.toString() === query.classId)
                        ?.description
                    }
                  </strong>
                </p>
              </RenderIf>
            </div>

            <RenderIf condition={isSubjectsLoading}>
              <p style={{ color: '#666' }}>Loading subjects...</p>
            </RenderIf>

            <RenderIf condition={!isSubjectsLoading && subjects?.length}>
              <p className="text-14 mb-20 text-grey">
                Click on a subject to continue
              </p>

              <div className="card-row-4">
                {subjects?.map((item) => (
                  <SBASubjectCard
                    key={item.subject.id}
                    subject={item.subject.description}
                    status={item.isCompleted ? 'completed' : ''}
                    onClick={() =>
                      history.push(
                        `/sba-view?${getQueryString({ ...query, subjectId: item.subject.id })}`,
                      )
                    }
                  />
                ))}
              </div>
            </RenderIf>

            <RenderIf
              condition={
                !isSubjectsLoading && !subjects?.length && query.classId
              }
            >
              <p style={{ color: '#666' }}>
                No subjects found for the selected class.
              </p>
            </RenderIf>
          </RenderIf>
        </Tab>
      </Tabs>

      <CreateSBAModal
        isOpen={isOpen === 'create-sba'}
        closeModal={() => setIsOpen('')}
      />
    </PageLayout>
  );
}

function SBASubjectCard({ subject, status, onClick }) {
  return (
    <div className="sba-subject-card pointer" onClick={onClick}>
      <div className="d-flex justify-between align-items-center">
        <Subject />
        <RenderIf condition={!!status}>
          <Tag
            className={`status-tag ${status.toLowerCase()}`}
            type={statusType[status.toLowerCase()]}
          >
            {status.toUpperCase()}
          </Tag>
        </RenderIf>
      </div>
      <p className="text-20 font-500 mt-20">{subject}</p>
    </div>
  );
}

function SBAClassCard({ classItem, onClick }) {
  return (
    <div className="sba-class-card pointer" onClick={onClick}>
      <div className="d-flex justify-between align-items-center">
        <Certificate />
      </div>
      <p className="text-20 font-500 mt-20">{classItem.description}</p>
      <p className="text-14 text-grey mt-8">View subjects</p>
    </div>
  );
}

const statusType = {
  completed: 'green',
  draft: 'gray',
};
